import { useVariant } from '@calendly/feature-check-react';
import { SearchForm } from '@calendly/form';
import { Loader } from '@calendly/ui/components/loader';
import { Pagination } from '@calendly/ui/components/pagination';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { redirectTo } from 'actions/core/navigation';
import LoadingState from 'components/core/loading_state';
import { EMPTY_STATE_TYPES, EmptyState } from 'components/empty_state';
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state';
import { ExportButton } from 'features/event_type_export';
import { UpgradeBanner } from 'features/upgrade_banner';
import { setLocalStorageAlert } from 'hooks/use_local_storage_alerts';
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { selectAllManagedEventTypes, useFetchManagedEventTypesPolicyQuery, useFetchManagedEventTypesQuery } from './api';
import { IntroState } from './intro_state';
import { ManagedEventTypesTable } from './managed_event_types_table';
import { NotAvailableBanner } from './not_available_banner';
import { isManagedEventTypesFeatureEnabledSelector } from './selectors';
import { useManagedEventTypeQueryParams } from './use_managed_event_type_query_params';
const controlsIsPhoneCss = "cx7azcc";
const controlsCss = "c1l2p9vt";
const Controls = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(controlsCss, {
    [controlsIsPhoneCss]: isPhone
  }, props.className)} />;
});
const searchContainerCss = "sjao40w";
const SearchContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(searchContainerCss, props.className)} />;
});
const actionsContainerIsTabletDownCss = "avsoffg";
const actionsContainerCss = "a1u3c0iy";
const ActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(actionsContainerCss, {
    [actionsContainerIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const fieldsetCss = "f1hq7hm3";
const Fieldset = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'fieldset';
  return <Component ref={ref} {...restProps} className={classNames(fieldsetCss, props.className)} />;
});
const paginationContainerCss = "p19x2rqc";
const PaginationContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(paginationContainerCss, props.className)} />;
});
const emptyStateContainerCss = "er6zdfm";
const EmptyStateContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyStateContainerCss, props.className)} />;
});
const ManagedEventTypeBanner = ({
  disabled
}) => {
  if (disabled) {
    return <NotAvailableBanner />;
  }
  return null;
};
const ListContent = () => {
  const metPaywallDelayExperiment = useVariant('met_paywall_delay_experiment');
  const shouldDelayPaywall = metPaywallDelayExperiment === 'on';
  const isManagedEventTypesFeatureEnabled = useSelector(isManagedEventTypesFeatureEnabledSelector);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const [managedEventsQueryParams, updateQueryParams] = useManagedEventTypeQueryParams();
  const {
    filterTerm,
    page: queryPageValue
  } = managedEventsQueryParams;
  const {
    data: responseData,
    isLoading,
    isSuccess,
    isFetching
  } = useFetchManagedEventTypesQuery({
    ...managedEventsQueryParams
  });
  const pagination = responseData && responseData.pagination;
  const {
    currentPage,
    totalPages
  } = pagination || {};
  const managedEventTypes = responseData && selectAllManagedEventTypes(responseData.collection);
  const isEmpty = !filterTerm && managedEventTypes?.length === 0;
  const disabled = !isManagedEventTypesFeatureEnabled && !shouldDelayPaywall;
  const isNewEmptyStateEnabled = useNewEmptyStates();
  const noManagedEventTypes = managedEventTypes && managedEventTypes.length === 0;
  useEffect(() => {
    if (noManagedEventTypes && queryPageValue && queryPageValue > totalPages) {
      updateQueryParams({
        ...managedEventsQueryParams,
        page: 1
      });
    }
  }, [updateQueryParams, noManagedEventTypes, queryPageValue, totalPages, managedEventsQueryParams]);
  const searchHandler = newFilterTerm => {
    updateQueryParams({
      filterTerm: newFilterTerm
    });
  };
  const paginationHandler = nextPage => {
    updateQueryParams({
      ...managedEventsQueryParams,
      page: nextPage
    });
  };
  if (isLoading && !isSuccess) return <Loader />;
  if (!isFetching && !filterTerm && noManagedEventTypes && disabled) {
    if (isNewEmptyStateEnabled) {
      return <EmptyStateContainer>
          <UpgradeBanner featureKey="managed_events" contentKey={CONTENT_PAGES.managedEventTypes} />
          <EmptyState type={EMPTY_STATE_TYPES.centered} contentKey={CONTENT_PAGES.managedEventTypes} featureKey="managed_events" options={{
          url: '/app/admin/managed_event_types/new'
        }} />
        </EmptyStateContainer>;
    }
    return <IntroState />;
  }
  return <>
      <ManagedEventTypeBanner disabled={disabled} />
      <Fieldset>
        {(!isEmpty || !isNewEmptyStateEnabled) && <Controls>
            <SearchContainer>
              {(!isNewEmptyStateEnabled || managedEventTypes?.length >= 5) && <SearchForm onSearch={searchHandler} initialValue={filterTerm} disabled={disabled} />}
            </SearchContainer>
            <ActionsContainer>
              {isNewEmptyStateEnabled && isAdminOrOwner && <ExportButton disabled={disabled} />}
            </ActionsContainer>
          </Controls>}
        <ManagedEventTypesTable disabled={disabled} />
        <PaginationContainer>
          <Pagination {...pagination} currentPage={Number(queryPageValue) || currentPage} onPageClick={paginationHandler} />
        </PaginationContainer>
      </Fieldset>
    </>;
};
export const Content = () => {
  const dispatch = useDispatch();
  const {
    isFetching,
    isSuccess,
    data: policyData
  } = useFetchManagedEventTypesPolicyQuery();
  const canViewManagedEventTypes = policyData?.canViewManagedEventTypes === true;
  const meetingTypesPath = useMeetingTypesPath();
  if (isFetching) {
    return <LoadingState />;
  }
  if (isSuccess && !canViewManagedEventTypes) {
    if (typeof policyData?.canViewManagedEventTypes === 'string') {
      setLocalStorageAlert(policyData?.canViewManagedEventTypes);
    }
    dispatch(redirectTo(meetingTypesPath));
  }
  return canViewManagedEventTypes ? <ListContent /> : <LoadingState />;
};

require("./content.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./content.js");